import { LazyLoader } from '@NextSkyTheme/lazy-load';

(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Shared cart helpers - do not depend on the bundle-upsell block config,
  // nor on whether the block is even rendered on the page. Used both by the
  // block UI (product page only, see DOMContentLoaded below) and by the
  // generic orphaned-bundle-item cleanup, which runs EVERYWHERE (see
  // initGenericBundleCleanup) - e.g. on /cart.
  // ---------------------------------------------------------------------

  function getCartElement() {
    // main-cart only exists on the /cart page - it takes priority there,
    // because that's the visible cart UI (cart-drawer, rendered globally on
    // every page via overlay-group, is just a hidden background panel at
    // that point). main-cart never exists on other pages, so behavior on
    // PDP/collection/etc. stays unchanged.
    return document.querySelector('main-cart') || document.querySelector('cart-drawer');
  }

  function getSectionFragment(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc.querySelector('.shopify-section');
  }

  // The cart count badge appears twice in the header (desktop/mobile), but
  // Shopify only returns one shared HTML version of the "cart-icon-bubble"
  // section - after swapping it in, we copy the state to the mobile version too.
  function syncCartIconBubbles() {
    var primary = document.getElementById('cart-icon-bubble');
    var mobile = document.getElementById('cart-icon-bubble-mobile');
    if (!primary || !mobile) return;
    var badge = primary.querySelector('.cart-count');
    var mobileBadge = mobile.querySelector('.cart-count');
    if (badge && mobileBadge) {
      mobileBadge.innerHTML = badge.innerHTML;
    }
  }

  // Generic cart section swap based on cart.getSectionsToRender() - used for
  // main-cart (the /cart page), which in this theme has NO renderContents()
  // method of its own (unlike cart-drawer).
  function renderSectionsGeneric(cart, response) {
    cart.getSectionsToRender().forEach(function (section) {
      var html = response.sections[section.id];
      if (!html) return;
      var sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      var newNode = getSectionFragment(html, section.selector);
      if (!newNode) return;
      sectionElement.innerHTML = newNode.innerHTML;
    });
    syncCartIconBubbles();
  }

  // Safely renders a /cart/update.js or /cart/add.js response into the cart UI.
  // For cart-drawer it uses the theme's native cart.renderContents() (proven,
  // handles extra cases like the recommendations section) - but ONLY when the
  // cart did NOT become empty as a result of our operation, because the native
  // method checks cart emptiness against the OLD DOM state instead of the new
  // server response, and blows up on that transition (querySelector returns
  // null, .innerHTML throws a TypeError). We detect that case ourselves from
  // response.item_count and render the empty state manually. For main-cart
  // (the /cart page, no native renderContents()) we always use our own generic
  // renderer (renderSectionsGeneric) for both branches, empty and non-empty.
  function safeRenderCartContents(cart, response) {
    if (!cart || typeof cart.getSectionsToRender !== 'function' || !response || !response.sections) return;

    try {
      var isDrawer = cart.tagName === 'CART-DRAWER';
      var sectionId = cart.sectionId;
      var sectionHtml = sectionId ? response.sections[sectionId] : null;
      var emptySelector = isDrawer ? '.drawer__inner-empty' : '.is-empty';
      var bodySelector = isDrawer ? '.drawer__body' : '.main-cart__wrapper';

      var becameEmpty = response.item_count === 0 && sectionHtml && getSectionFragment(sectionHtml, emptySelector);

      if (becameEmpty) {
        var newBody = getSectionFragment(sectionHtml, bodySelector);
        var currentBody = isDrawer ? cart.querySelector(bodySelector) : cart.closest(bodySelector);
        if (newBody && currentBody) {
          currentBody.innerHTML = newBody.innerHTML;
          // The empty-cart markup includes lazy-loaded collection tile images
          // (.image-lazy-load, srcset held in data-srcset until an
          // IntersectionObserver promotes it) - same pattern the theme uses
          // everywhere else after injecting new HTML. Without this, images
          // inserted via innerHTML have no observer watching them and never load.
          new LazyLoader('.image-lazy-load');
        }

        var cartIconHtml = response.sections['cart-icon-bubble'];
        var cartIconSection = document.getElementById('cart-icon-bubble');
        if (cartIconSection && cartIconHtml) {
          var iconNode = getSectionFragment(cartIconHtml, null);
          if (iconNode) cartIconSection.innerHTML = iconNode.innerHTML;
        }
        syncCartIconBubbles();
        return;
      }

      if (isDrawer && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
        return;
      }

      renderSectionsGeneric(cart, response);
    } catch (renderErr) {
      console.error('bundle-upsell: error refreshing cart', renderErr);
    }
  }

  // Coalesces overlapping callers into a single request: updateAllButtonStates()
  // and enforceBundleIntegrity() (among others) are frequently called together,
  // in the same reactive burst, each independently needing the current cart -
  // without this, every one of them would fire its own identical /cart.js GET.
  // While one fetch is in flight, later callers get the SAME promise instead
  // of starting a new request; once it settles, the next call starts fresh.
  var pendingCartFetch = null;
  function getCurrentCartJson() {
    if (pendingCartFetch) return pendingCartFetch;

    pendingCartFetch = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .finally(function () { pendingCartFetch = null; });

    return pendingCartFetch;
  }

  function getCurrentCartVariantIds() {
    return getCurrentCartJson()
      .then(function (cart) { return cart.items.map(function (item) { return item.variant_id; }); })
      .catch(function () {
        // if we can't check the cart, better to add everything than nothing
        return [];
      });
  }

  // /cart/change.js and /cart/update.js return the full, current cart state
  // (item_count + items[]). If we already have that object at hand (e.g. from
  // an intercepted fetch response), we pull the variant list from it instead
  // of doing another GET to /cart.js. Returns null if this isn't a full cart
  // object (e.g. a /cart/add.js response only contains the added items, not
  // the whole cart).
  function extractVariantIdsFromCartResponse(cartJson) {
    if (!cartJson || !Array.isArray(cartJson.items) || typeof cartJson.item_count !== 'number') {
      return null;
    }
    return cartJson.items.map(function (item) { return item.variant_id; });
  }

  // If we already know the variant list (e.g. from an intercepted fetch
  // response), use it right away. Otherwise do a fresh fetch to /cart.js.
  function resolveVariantIds(knownVariantIds) {
    if (Array.isArray(knownVariantIds)) return Promise.resolve(knownVariantIds);
    return getCurrentCartVariantIds();
  }

  // Removes the given variants from the cart (setting their quantity to 0) and,
  // if possible, immediately refreshes the cart UI (drawer/cart page) instead
  // of waiting for the next interaction or a page reload.
  function removeVariantsFromCart(variantIds) {
    if (!variantIds.length) return Promise.resolve(null);

    var updates = {};
    variantIds.forEach(function (id) {
      updates[id] = 0;
    });

    var cart = getCartElement();
    var payload = { updates: updates };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      try {
        payload.sections = cart.getSectionsToRender().map(function (s) { return s.section; });
        payload.sections_url = window.location.pathname;
      } catch (e) {
        // if we can't build the section list, just update without sections
      }
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        if (cart && payload.sections) {
          safeRenderCartContents(cart, response);
        }
        // response is already the full, current cart state - pass it along so
        // listeners (see cart:refresh/cart:updated below) can use it directly
        // instead of firing their own /cart.js GET just to get data we
        // already have.
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: response }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
        return response;
      })
      .catch(function (err) {
        console.error('bundle-upsell: error removing products from cart', err);
        // don't abort the whole flow - the caller may continue despite the
        // error (e.g. still try to add a new package)
        return null;
      });
  }

  // ---------------------------------------------------------------------
  // Generic cleanup of "orphaned" bundle items - works based on the hidden
  // cart line property _bundle_requires_variant (set in buildItemsForPackage
  // when a package is added on the product page), NOT based on the
  // bundle-upsell block config, which might not even exist here. This means
  // removing the main product on, say, the /cart page also dynamically
  // removes the linked package, without a page reload.
  // ---------------------------------------------------------------------

  var isCleaningOrphans = false;

  // Shared across ALL [data-bundle-upsell] roots on the page (not per-root) -
  // if the product page's own block AND the cart drawer's bundle
  // recommendation happen to both be bound at once (e.g. the same product
  // qualifies for both), each has its own enforceBundleIntegrity() reacting
  // to the very same cart mutation. Without a SHARED guard, both would
  // independently detect the same orphaned package and each fire its own
  // /cart/update.js removal at the same time - a visible flicker plus a
  // burst of redundant requests. Only one enforcement pass runs at a time;
  // the other roots' listeners get another chance on the next reactive
  // trigger (by which point the package is already gone, so they no-op).
  var isEnforcingIntegrity = false;

  function findOrphanedBundleVariantIds(cart) {
    var variantIds = cart.items.map(function (item) { return item.variant_id; });
    var idsToRemove = [];

    cart.items.forEach(function (item) {
      var requiredVariantId = item.properties && item.properties._bundle_requires_variant;
      if (!requiredVariantId) return;
      if (variantIds.indexOf(Number(requiredVariantId)) === -1 && idsToRemove.indexOf(item.variant_id) === -1) {
        idsToRemove.push(item.variant_id);
      }
    });

    return idsToRemove;
  }

  function cleanupOrphanedBundleItems(knownCart) {
    if (isCleaningOrphans) return Promise.resolve();

    var cartPromise = knownCart ? Promise.resolve(knownCart) : getCurrentCartJson().catch(function () { return null; });

    return cartPromise.then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return null;

      var idsToRemove = findOrphanedBundleVariantIds(cart);
      if (!idsToRemove.length) return null;

      isCleaningOrphans = true;
      console.info('bundle-upsell: main product removed from cart - removing orphaned package');
      return removeVariantsFromCart(idsToRemove).finally(function () {
        isCleaningOrphans = false;
      });
    });
  }

  // True if at least one [data-bundle-upsell] root on the page is already
  // bound - i.e. some root's own config-based enforceBundleIntegrity is
  // already covering the "main product removed -> remove linked package"
  // check for that root.
  function hasBoundBundleUpsellRoot() {
    return document.querySelector('[data-bundle-upsell-bound="true"]') !== null;
  }

  // onCartMutated (optional) fires on every detected cart change, BEFORE the
  // orphan-cleanup decision - used by the DOMContentLoaded handler below to
  // re-scan for [data-bundle-upsell] roots that don't exist in the DOM yet
  // at page-load time but can appear later purely via an AJAX cart mutation
  // (e.g. cart-drawer.liquid only renders its bundle recommendation markup
  // in the "cart is not empty" branch - if the cart starts out empty, that
  // root, and its config/buttons, simply doesn't exist until something gets
  // added, and a one-time DOMContentLoaded scan would never find it).
  function initGenericBundleCleanup(onCartMutated) {
    // Document-level guard (there's no block root here) - so we don't attach
    // the same interceptor/listeners twice.
    if (document.documentElement.dataset.bundleCleanupBound === 'true') return;
    document.documentElement.dataset.bundleCleanupBound = 'true';

    function react(knownCart) {
      if (typeof onCartMutated === 'function') onCartMutated();

      // Only fall back to this generic, property-based cleanup when NO root
      // on the page is already bound (e.g. the product page's own
      // bundle-upsell block, or the cart drawer's bundle recommendation once
      // bound) - otherwise both mechanisms independently detect and remove
      // the very same orphaned package, each firing its own /cart/update.js
      // call and re-triggering the other, causing a visible flicker and a
      // burst of redundant network requests.
      if (!hasBoundBundleUpsellRoot()) {
        cleanupOrphanedBundleItems(knownCart);
      }
    }

    react();

    // If the event carries the current cart (see removeVariantsFromCart's
    // dispatch), use it directly instead of firing our own /cart.js GET just
    // to get data we already have. Events dispatched elsewhere without a
    // detail (evt.detail undefined) fall back to a fresh fetch as before.
    function reactToEvent(evt) {
      var cartJson = evt && evt.detail;
      var isFullCart = cartJson && Array.isArray(cartJson.items) && typeof cartJson.item_count === 'number';
      react(isFullCart ? cartJson : null);
    }
    document.addEventListener('cart:refresh', reactToEvent);
    document.addEventListener('cart:updated', reactToEvent);

    var originalFetch = window.fetch;
    window.fetch = function () {
      var result = originalFetch.apply(this, arguments);
      var url = arguments[0];
      if (
        typeof url === 'string' &&
        (url.indexOf('/cart/add') !== -1 ||
          url.indexOf('/cart/change') !== -1 ||
          url.indexOf('/cart/update') !== -1)
      ) {
        result
          .then(function (response) { return response.clone().json().catch(function () { return null; }); })
          .then(function (cartJson) {
            var isFullCart = cartJson && Array.isArray(cartJson.items) && typeof cartJson.item_count === 'number';
            react(isFullCart ? cartJson : null);
          });
      }
      return result;
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
    // More than one instance can exist on the same page at once - e.g. the
    // product page's own bundle-upsell block AND the cart drawer's bundle
    // recommendation override (see cart-drawer.liquid) rendered together.
    // Each root gets its own fully independent instance below. Re-running
    // this scan is cheap and safe for roots that already exist - init is a
    // no-op for anything already marked bundleUpsellBound.
    function scanForRoots() {
      document.querySelectorAll('[data-bundle-upsell]').forEach(initBundleUpsellInstance);
    }

    scanForRoots();

    // Always on: drives both the generic orphaned-item cleanup (works on any
    // page, e.g. /cart) AND re-scanning for roots that appear later purely
    // via an AJAX cart mutation (see the onCartMutated comment above
    // initGenericBundleCleanup).
    initGenericBundleCleanup(scanForRoots);

    function initBundleUpsellInstance(root) {
    // Guard against double init - if this script somehow runs more than once
    // (e.g. the <script> tag gets re-injected on a section refresh in the
    // Theme Editor), don't attach the same listeners a second time.
    if (root.dataset.bundleUpsellBound === 'true') return;
    root.dataset.bundleUpsellBound = 'true';

    // config is re-read (not just parsed once) because this root's content
    // can be swapped via AJAX after the initial page load without a fresh
    // DOMContentLoaded (e.g. the cart drawer's bundle recommendation card,
    // which only becomes eligible once the right product is added to the
    // cart - see cart-drawer.liquid). Reading it fresh on every reactive
    // cart-change trigger (see refreshConfig() calls below) means this
    // instance picks up the real config as soon as it appears, instead of
    // staying bound to whatever was present (or absent) at page load.
    var config = {};
    function refreshConfig() {
      var configEl = root.querySelector('[data-bundle-upsell-config]');
      if (!configEl) {
        config = {};
        return;
      }
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('bundle-upsell: failed to parse config', e);
        config = {};
      }
    }
    refreshConfig();

    // Extra guard at the click level - even if two listeners somehow got
    // attached, this flag prevents a double submit within the same cycle.
    var isSubmitting = false;

    root.addEventListener('click', function (evt) {
      var btn = evt.target.closest('[data-bundle-add-btn]');
      if (!btn) return;

      // Make sure we're acting on the freshest config in case this root's
      // content changed since the last reactive refresh (see refreshConfig
      // above).
      refreshConfig();

      // The product in this package is unavailable (0 inventory / no
      // "continue selling") - the button is disabled from Liquid already,
      // but this is an extra guard in case someone re-enables it via devtools.
      if (isPackageUnavailable(btn.getAttribute('data-package-key'))) return;

      if (isSubmitting) return;
      isSubmitting = true;

      var pkgKey = btn.getAttribute('data-package-key');
      var items = buildItemsForPackage(pkgKey);

      if (!items || !items.length) {
        console.warn('bundle-upsell: no products for package', pkgKey);
        isSubmitting = false;
        return;
      }

      addBundleToCart(items, btn, pkgKey).finally(function () {
        isSubmitting = false;
      });
    });

    var translations = window.cartTranslations || {
      alreadyInCart: 'Już w koszyku',
      addToCart: 'Dodaj do koszyka'
    };

    function isPackageUnavailable(pkgKey) {
      var pkg = config[pkgKey];
      if (!pkg || pkg.available === false) return true;

      // If the current product is set to be added to the package (configured
      // in the panel via "current product's collection"), but it's itself
      // sold out - block the whole package, since the customer wouldn't get
      // the complete set they were promised anyway.
      if (config.currentProduct && config.currentProduct.available === false) return true;

      return false;
    }

    function buildItemsForPackage(pkgKey) {
      var pkg = config[pkgKey];
      if (!pkg) return [];

      var packageItems = (pkg.items || []).map(function (item) {
        if (!config.currentProduct) return item;
        // Tag every package item with a hidden cart line property pointing to
        // the main product's variant that this package depends on. Shopify
        // hides properties starting with "_" from the customer, so nothing
        // changes visually. This means the "this product requires that one in
        // the cart" link travels along with /cart.js and can be read by the
        // generic cleanup (initGenericBundleCleanup) on ANY page - not just
        // the one where the block is rendered (e.g. the cart page).
        return {
          id: item.id,
          quantity: item.quantity,
          properties: { _bundle_requires_variant: String(config.currentProduct.id) }
        };
      });

      var items = packageItems;

      // Only add the current product if it's actually available - if it's
      // sold out, just skip including it instead of blocking the whole
      // package (the package can still be available without it).
      if (config.currentProduct && config.currentProduct.available !== false) {
        var alreadyIncluded = packageItems.some(function (item) {
          return item.id === config.currentProduct.id;
        });
        if (!alreadyIncluded) {
          // The cart renders lines in the order they were created (oldest
          // first, on top) - neither main-cart-items.liquid nor
          // cart-drawer.liquid reverses cart.items. Putting the current
          // product FIRST in this array means, when both are added together
          // in one /cart/add.js call, its line is created first and lands
          // above the package's - the course the customer picked stays the
          // lead item, with the package (meaningless on its own) directly
          // under it.
          items = [config.currentProduct].concat(packageItems);
        }
      }

      return items;
    }

    // Returns the list of all package keys defined in the config (i.e.
    // everything except "currentProduct"), regardless of how many there are -
    // so we don't hardcode 'standard'/'premium' in the exclusion logic.
    function getAllPackageKeys() {
      return Object.keys(config).filter(function (key) {
        return key !== 'currentProduct';
      });
    }

    // Returns the variant ids belonging to packages OTHER than pkgKey - i.e.
    // the ones that need to be removed from the cart so the customer doesn't
    // end up with both STANDARD and PREMIUM at once. The current product
    // (currentProduct) is shared between both packages, so it's never
    // included here - it stays in the cart when switching. A variant that
    // also happens to appear in the target package's OWN items (e.g. the
    // admin picked the same product in both packages by mistake) is excluded
    // from removal, so it doesn't get deleted right before we re-add it.
    function getOtherPackageVariantIds(pkgKey) {
      var targetIds = ((config[pkgKey] && config[pkgKey].items) || []).map(function (item) {
        return item.id;
      });

      var otherIds = [];
      getAllPackageKeys().forEach(function (key) {
        if (key === pkgKey) return;
        var pkg = config[key];
        if (!pkg || !pkg.items) return;
        pkg.items.forEach(function (item) {
          if (targetIds.indexOf(item.id) === -1 && otherIds.indexOf(item.id) === -1) {
            otherIds.push(item.id);
          }
        });
      });

      return otherIds;
    }

    function updateButtonState(btn, isInCart) {
      var pkgKey = btn.getAttribute('data-package-key');
      var labelDefault = btn.dataset.labelDefault || btn.textContent.trim();
      var labelUnavailable = btn.dataset.labelUnavailable || 'Produkt niedostępny';

      if (!btn.dataset.originalText) {
        btn.dataset.originalText = labelDefault;
      }

      // State priority: unavailable > already in cart > default.
      // "Unavailable" overrides everything else - no point showing "Already
      // in cart" if the product can't be added again anyway.
      if (isPackageUnavailable(pkgKey)) {
        btn.disabled = true;
        btn.classList.remove('btn-in-cart');
        btn.classList.add('btn-unavailable');
        btn.setAttribute('aria-label', labelUnavailable);
        btn.textContent = labelUnavailable;
        return;
      }

      if (isInCart) {
        btn.disabled = true;
        btn.classList.remove('btn-unavailable');
        btn.classList.add('btn-in-cart');
        btn.setAttribute('aria-label', translations.alreadyInCart);
        btn.textContent = translations.alreadyInCart;
      } else {
        btn.disabled = false;
        btn.classList.remove('btn-in-cart', 'btn-unavailable');
        btn.removeAttribute('aria-label');
        btn.textContent = btn.dataset.originalText;
      }
    }

    // Checks whether the main (current) product is still in the cart. If the
    // bundle requires its presence (config.currentProduct is configured on
    // this product page) and someone removed it from the cart - also removes
    // all STANDARD/PREMIUM package variants, so the customer can't end up
    // with just the package and no main product.
    //
    // This check only works on THIS product page (since only here do we have
    // the block config) - the equivalent for other pages (e.g. /cart) is the
    // generic initGenericBundleCleanup, based on the _bundle_requires_variant
    // cart line property (see buildItemsForPackage above).
    function enforceBundleIntegrity(knownVariantIds) {
      if (!config.currentProduct) return Promise.resolve();
      // Shared across all roots (see isEnforcingIntegrity above) - prevents
      // two independently-bound roots from both removing the same orphaned
      // package at once.
      if (isEnforcingIntegrity) return Promise.resolve();
      isEnforcingIntegrity = true;

      return resolveVariantIds(knownVariantIds).then(function (existingVariantIds) {
        var mainProductInCart = existingVariantIds.indexOf(config.currentProduct.id) !== -1;
        if (mainProductInCart) return null;

        var idsToRemove = [];
        getAllPackageKeys().forEach(function (key) {
          var pkg = config[key];
          if (!pkg || !pkg.items) return;
          pkg.items.forEach(function (item) {
            if (existingVariantIds.indexOf(item.id) !== -1 && idsToRemove.indexOf(item.id) === -1) {
              idsToRemove.push(item.id);
            }
          });
        });

        if (!idsToRemove.length) return null;

        console.info('bundle-upsell: main product removed from cart - removing linked package');
        return removeVariantsFromCart(idsToRemove).then(function (response) {
          // response is already the full cart state after removing the
          // package - use it right away instead of doing another GET to /cart.js.
          updateAllButtonStates(extractVariantIdsFromCartResponse(response));
        });
      }).finally(function () {
        isEnforcingIntegrity = false;
      });
    }

    function updateAllButtonStates(knownVariantIds) {
      var buttons = root.querySelectorAll('[data-bundle-add-btn]');
      if (!buttons.length) return;

      resolveVariantIds(knownVariantIds).then(function (existingVariantIds) {
        buttons.forEach(function (btn) {
          var pkgKey = btn.getAttribute('data-package-key');

          // Unavailable packages don't need a cart check - the "unavailable"
          // state wins regardless, so we set it right away and skip the rest.
          if (isPackageUnavailable(pkgKey)) {
            updateButtonState(btn, false);
            return;
          }

          var items = buildItemsForPackage(pkgKey);
          if (!items || !items.length) return;

          var allInCart = items.every(function (item) {
            return existingVariantIds.indexOf(item.id) !== -1;
          });

          updateButtonState(btn, allInCart);
        });
      });
    }

    // Re-reads config from the DOM before reacting to a cart change - see the
    // refreshConfig() comment above for why this needs to happen on every
    // reactive trigger, not just once at init.
    function refreshAndReact(knownVariantIds) {
      refreshConfig();
      updateAllButtonStates(knownVariantIds);
      enforceBundleIntegrity(knownVariantIds);
    }

    // Initial state on page load.
    refreshAndReact();

    // We hook into the same events as the rest of the theme (see CartDuplicateHandler).
    // If the event carries the current cart (see removeVariantsFromCart's
    // dispatch), use it directly instead of firing our own /cart.js GET.
    // Events dispatched elsewhere without a detail fall back to a fresh fetch.
    function reactToCartEvent(evt) {
      refreshAndReact(extractVariantIdsFromCartResponse(evt && evt.detail));
    }
    document.addEventListener('cart:refresh', reactToCartEvent);
    document.addEventListener('cart:updated', reactToCartEvent);

    // Additionally: catch EVERY cart change (add/remove/quantity change), no
    // matter where it comes from (drawer, quantity input, our own button) -
    // because 'cart:refresh'/'cart:updated' aren't always dispatched (e.g.
    // when removing from the drawer, CartDuplicateHandler only catches it via
    // its own internal mechanism).
    (function interceptCartRequests() {
      var originalFetch = window.fetch;
      window.fetch = function () {
        var result = originalFetch.apply(this, arguments);
        var url = arguments[0];
        if (
          typeof url === 'string' &&
          (url.indexOf('/cart/add') !== -1 ||
            url.indexOf('/cart/change') !== -1 ||
            url.indexOf('/cart/update') !== -1)
        ) {
          result
            .then(function (response) {
              // Clone the response - the original stream will still be
              // consumed by the code that called this fetch (e.g. the
              // theme's native remove button in the drawer).
              return response.clone().json().catch(function () { return null; });
            })
            .then(function (cartJson) {
              // /cart/change.js and /cart/update.js already return the full
              // cart state - use it right away, without an extra GET to
              // /cart.js and without an artificial delay. For /cart/add.js
              // (which doesn't return the full cart) extractVariantIdsFromCartResponse
              // returns null and we fall back to a fresh fetch inside resolveVariantIds.
              var existingVariantIds = extractVariantIdsFromCartResponse(cartJson);
              refreshAndReact(existingVariantIds);
            });
        }
        return result;
      };
    })();

    function addBundleToCart(items, btn, pkgKey) {
      var originalText = btn.textContent;
      btn.disabled = true;
      btn.classList.add('loading');

      var otherVariantIds = getOtherPackageVariantIds(pkgKey);

      // First remove any products belonging to the OTHER package (if they
      // were there) - the customer can't have both STANDARD and PREMIUM at
      // once. Only after this step do we check what actually needs adding.
      return removeVariantsFromCart(otherVariantIds).then(function () {
        return getCurrentCartVariantIds();
      }).then(function (existingVariantIds) {
        var itemsToAdd = items.filter(function (item) {
          return existingVariantIds.indexOf(item.id) === -1;
        });

        var skippedCount = items.length - itemsToAdd.length;

        if (itemsToAdd.length === 0) {
          // All products from the package are already in the cart - add
          // nothing, just open the drawer so the customer sees what they have.
          btn.disabled = false;
          btn.classList.remove('loading');
          btn.textContent = originalText;
          openCartDrawer();
          updateAllButtonStates();
          return;
        }

        if (skippedCount > 0) {
          // TODO: optionally show a message like "2 of 3 products were already in the cart"
          console.info('bundle-upsell: skipped ' + skippedCount + ' product(s) already in cart');
        }

        return sendAddToCart(itemsToAdd, btn, originalText);
      });
    }

    function sendAddToCart(items, btn, originalText) {
      var cart = getCartElement();
      var payload = { items: items };

      // Same as the standard product form: pull in the updated sections right
      // away (drawer, cart count, etc.) instead of making a second request.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        try {
          payload.sections = cart.getSectionsToRender().map(function (s) { return s.section; });
          payload.sections_url = window.location.pathname;
        } catch (e) {
          // if we can't build the section list, just add without sections
        }
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw err;
            });
          }
          return res.json();
        })
        .then(function (response) {
          if (cart && payload.sections) {
            safeRenderCartContents(cart, response);
          }
          openCartDrawer();
          // updateAllButtonStates sets the correct final button state (e.g.
          // "Already in cart") - so we don't force disabled/textContent
          // manually in finally below, to avoid overwriting it.
          updateAllButtonStates();
        })
        .catch(function (err) {
          console.error('bundle-upsell: error adding to cart', err);
          // The product might have just sold out at this exact moment (race
          // condition between our availability check and the click) -
          // refresh state to immediately show "Unavailable" instead of
          // leaving the button with no feedback at all.
          updateAllButtonStates();
        })
        .finally(function () {
          // Only clear the loading state - the target text/disabled is
          // already handled by updateAllButtonStates() called in .then/.catch above.
          btn.classList.remove('loading');
        });
    }

    function openCartDrawer() {
      // The theme opens the drawer via a built-in click listener on the cart
      // icon (CartDrawer.connectedCallback) - we simulate the same thing
      // instead of calling eventModal directly (a module we don't have
      // access to from this file).
      var trigger = document.getElementById('cart-icon-bubble') || document.getElementById('cart-icon-bubble-mobile');
      if (trigger) {
        trigger.click();
      }
    }
    }
  });
})();
